import { RatingCategory } from '../types/review';
import {
  computeOverallRating,
  STANDARD_RATING_CATEGORIES,
  type StandardRatingCategory,
} from './ratingScore';
import { getRankedPlatforms } from '../data/platformRanking';

export interface PlatformRatings {
  rating: number;
  categories: RatingCategory[];
  lastTested?: string;
}

function scored(
  scores: Record<StandardRatingCategory, number>,
  lastTested?: string
): PlatformRatings {
  const categories = STANDARD_RATING_CATEGORIES.map((name) => ({
    name,
    score: scores[name],
  }));

  return {
    rating: computeOverallRating(categories),
    categories,
    lastTested,
  };
}

/**
 * Scores for reviewed platforms that are not on the overall ranking.
 * Ranked platforms take their scores from src/data/platformRanking.ts.
 */
const unrankedCategoryScores: Record<string, PlatformRatings> = {
  'dondi-ai-review': scored(
    {
      'Chat Quality': 4.3,
      'Image Quality': 4.2,
      Features: 4.5,
      'Generative Tools': 4.4,
      'User Experience': 4.1,
      'Value/Money': 3.8,
      Characters: 4.4,
      Customization: 4.6,
      'Advanced Content': 4.3,
      'Free Trial': 3.7,
    },
    '2026-08-10'
  ),
  'secrets-ai-review': scored(
    {
      'Chat Quality': 4.2,
      'Image Quality': 4.1,
      Features: 4.4,
      'Generative Tools': 4.3,
      'User Experience': 4.0,
      'Value/Money': 3.5,
      Characters: 4.2,
      Customization: 4.3,
      'Advanced Content': 4.4,
      'Free Trial': 3.9,
    },
    '2026-08-10'
  ),
  'kalon-ai-review': scored(
    {
      'Chat Quality': 4.1,
      'Image Quality': 4.0,
      Features: 4.3,
      'Generative Tools': 4.2,
      'User Experience': 4.0,
      'Value/Money': 3.6,
      Characters: 4.1,
      Customization: 4.5,
      'Advanced Content': 4.2,
      'Free Trial': 3.8,
    },
    '2026-08-10'
  ),
  'kissable-review': scored(
    {
      'Chat Quality': 4.1,
      'Image Quality': 3.9,
      Features: 4.3,
      'Generative Tools': 4.0,
      'User Experience': 4.0,
      'Value/Money': 3.5,
      Characters: 3.9,
      Customization: 4.1,
      'Advanced Content': 4.0,
      'Free Trial': 3.4,
    },
    '2026-08-10'
  ),
  'lollipop-chat-review': scored(
    {
      'Chat Quality': 2.3,
      'Image Quality': 4.1,
      Features: 2.5,
      'Generative Tools': 1.0,
      'User Experience': 3.0,
      'Value/Money': 1.5,
      Characters: 2.0,
      Customization: 1.2,
      'Advanced Content': 0.0,
      'Free Trial': 3.8,
    },
    '2026-10-29'
  ),
  'eternal-ai-review': scored(
    {
      'Chat Quality': 3.5,
      'Image Quality': 4.5,
      Features: 3.2,
      'Generative Tools': 2.0,
      'User Experience': 2.7,
      'Value/Money': 3.1,
      Characters: 3.0,
      Customization: 1.8,
      'Advanced Content': 0.0,
      'Free Trial': 4.8,
    },
    '2026-11-04'
  ),
};

const rankedCategoryScores: Record<string, PlatformRatings> = Object.fromEntries(
  getRankedPlatforms().map(({ slug, rating, categories, lastTested }) => [
    slug,
    { rating, categories, lastTested },
  ])
);

export const bestAIGirlfriendRatings: Record<string, PlatformRatings> = {
  ...unrankedCategoryScores,
  ...rankedCategoryScores,
};

const GRID_CARD_CATEGORIES = [
  'Image Quality',
  'User Experience',
  'Value/Money',
  'Advanced Content',
] as const;

export function getPlatformCategories(slug: string): RatingCategory[] {
  return bestAIGirlfriendRatings[slug]?.categories ?? [];
}

export function getRatingBreakdown(slug: string): RatingCategory[] {
  const categories = getPlatformCategories(slug);
  if (categories.length === 0) {
    return [];
  }

  return GRID_CARD_CATEGORIES.map((name) => {
    const category = categories.find((item) => item.name === name);
    return category ?? { name, score: 0 };
  }).filter((category) => category.score > 0);
}

export function getPlatformRating(slug: string): number {
  return bestAIGirlfriendRatings[slug]?.rating || 0;
}

export function getPlatformLastTested(slug: string): string | undefined {
  return bestAIGirlfriendRatings[slug]?.lastTested;
}
